import { clientDay } from '../../shared/dateTime';
import { plural } from '../../shared/plural';

/**
 * Statystyka cen z VIA (13.10.2026, plan scoringu — część A) — odczyt pola
 * `priceStatistics` z API (karta, wiersz listy, rekomendacje).
 *
 * Reguła żyje w API (`apps/api/src/price-statistics/`): cena klienta za km
 * (stawka zlecenia z TMS / dystans trasy, w EUR kursem NBP z dnia
 * załadunku) wobec mediany relacji, klienta i spółki. Tu jest tylko odczyt
 * i słowa dla operatora — bez React-a, żeby komórka listy, karta
 * i rekomendacje czytały to tak samo. Pole przychodzi wyłącznie przy
 * włączonym przełączniku `priceStatistics`.
 */

export const FEATURE_PRICE_STATISTICS = 'priceStatistics';

export type PriceLevelKind = 'relation' | 'contractor' | 'company' | 'all';

export interface PriceLevel {
  level: PriceLevelKind;
  scope: string;
  key: string | null;
  label: string;
  orders: number;
  enough: boolean;
  medianEurPerKm: number | null;
  q1EurPerKm: number | null;
  q3EurPerKm: number | null;
  periodFrom: string | null;
  periodTo: string | null;
  diffPct: number | null;
}

/**
 * Skąd dystans ceny za km (16.10.2026): `tms` — z TMS, jak w statystyce;
 * `here` — trasa wg mapy (wiersz z prezlecenia bez dystansu z TMS);
 * `estimate` — szacunek z punktów (linia prosta × mnożnik).
 */
export interface PriceDistance {
  km: number;
  source: 'tms' | 'here' | 'estimate';
  label: string;
  straightKm: number | null;
  multiplier: number | null;
}

export interface OwnCostVerdict {
  belowCost: boolean | null;
  marginPln: number | null;
  text: string;
}

export interface PriceComparison {
  eurPerKm: number | null;
  ownMissing: string | null;
  rateNote: string | null;
  levels: PriceLevel[];
  minOrders: number;
  computedAt: string | null;
  text: string;
  basis: string;
  /** Skąd dystans ceny za km; `null` — nieznany albo starsza odpowiedź API. */
  distance?: PriceDistance | null;
  /** Tylko w rekomendacjach: koszt własny pierwszego auta z listy. */
  ownCost?: OwnCostVerdict | null;
}

/** Pole `priceStatistics` z rekordu; `null`, gdy go nie ma albo ma zły kształt. */
export function priceComparisonOf(
  record: Record<string, unknown> | undefined | null,
): PriceComparison | null {
  const v = record?.priceStatistics;
  if (!v || typeof v !== 'object') return null;
  const c = v as Partial<PriceComparison>;
  if (typeof c.text !== 'string') return null;
  return {
    eurPerKm: typeof c.eurPerKm === 'number' ? c.eurPerKm : null,
    ownMissing: typeof c.ownMissing === 'string' ? c.ownMissing : null,
    rateNote: typeof c.rateNote === 'string' ? c.rateNote : null,
    levels: Array.isArray(c.levels) ? c.levels : [],
    minOrders: typeof c.minOrders === 'number' ? c.minOrders : 0,
    computedAt: typeof c.computedAt === 'string' ? c.computedAt : null,
    text: c.text,
    basis: typeof c.basis === 'string' ? c.basis : '',
    distance: distanceOf(c.distance),
    ownCost:
      c.ownCost && typeof c.ownCost === 'object' && typeof c.ownCost.text === 'string'
        ? c.ownCost
        : null,
  };
}

function distanceOf(v: unknown): PriceDistance | null {
  if (!v || typeof v !== 'object') return null;
  const d = v as Partial<PriceDistance>;
  if (typeof d.km !== 'number' || (d.source !== 'tms' && d.source !== 'here' && d.source !== 'estimate')) {
    return null;
  }
  return {
    km: d.km,
    source: d.source,
    label: typeof d.label === 'string' ? d.label : '',
    straightKm: typeof d.straightKm === 'number' ? d.straightKm : null,
    multiplier: typeof d.multiplier === 'number' ? d.multiplier : null,
  };
}

/**
 * Pierwsza linia dymka, gdy dystans nie jest z TMS (16.10.2026): wiersz
 * z prezlecenia liczy cenę za km z trasy zlecenia, a mediany — wyłącznie
 * zlecenia z TMS. Dystans z TMS — `null` (jak dotąd, bez dopisku).
 */
export function distanceNote(c: PriceComparison): string | null {
  const d = c.distance;
  if (c.eurPerKm === null || !d || d.source === 'tms') return null;
  const km = d.km.toLocaleString('pl-PL', { maximumFractionDigits: 0 });
  return d.source === 'here'
    ? `Dystans wg mapy (${km} km), nie z TMS — zlecenie nie ma dystansu z TMS, a mediany liczą tylko zlecenia z TMS.`
    : `Dystans to szacunek z punktów (${km} km: linia prosta × mnożnik), nie z TMS ani z mapy — mediany liczą tylko zlecenia z TMS.`;
}

/** „1,35 €/km”. */
/** Sama liczba, „1,35” — w komórce listy, której nagłówek „€/km” niesie jednostkę. */
export function eurPerKmNumber(n: number): string {
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function eurPerKmText(n: number): string {
  return `${eurPerKmNumber(n)} €/km`;
}

/** „−8%”, „+5%”, „±0%”. */
export function diffText(pct: number): string {
  if (pct === 0) return '±0%';
  return `${pct < 0 ? '−' : '+'}${Math.abs(pct)}%`;
}

/** Poziom, wobec którego komórka listy pokazuje różnicę: relacja, a bez niej klient. */
function cellDiffLevel(c: PriceComparison): PriceLevel | null {
  if (c.eurPerKm === null) return null;
  const relation = c.levels.find((l) => l.level === 'relation');
  if (relation?.diffPct != null) return relation;
  const client = c.levels.find((l) => l.level === 'contractor');
  return client?.diffPct != null ? client : null;
}

/**
 * Druga linia komórki listy: sama różnica wobec mediany relacji, a bez
 * niej — klienta („−8%”); poniżej progu — „za mało”. `null` = nic do dodania.
 *
 * Poprawka po stagingu (14.10.2026): ze skrótami („−12% rel.”) kolumna
 * miała 67 px i lista przy 1366 px przewijała się w bok o 21–38 px (decyzja
 * użytkownika 08.10.2026: ma się mieścić). Sama różnica to ok. 33 px;
 * wobec czego — mówi dymek (`cellDiffBasis`), bez skrótów do zgadywania.
 */
export function cellDiffText(c: PriceComparison): string | null {
  const l = cellDiffLevel(c);
  if (l) return diffText(l.diffPct as number);
  return c.eurPerKm === null || c.computedAt === null ? null : 'za mało';
}

/** Zdanie do dymka komórki: wobec czego jest różnica pod liczbą. */
export function cellDiffBasis(c: PriceComparison): string | null {
  const l = cellDiffLevel(c);
  if (!l) return null;
  return l.level === 'relation'
    ? 'Pod liczbą: różnica wobec mediany relacji.'
    : 'Pod liczbą: różnica wobec mediany klienta (relacja bez wystarczających danych).';
}

/** „1 zlecenie”, „3 zlecenia”, „13 zleceń” — w dymku nie skracamy (14.10.2026). */
const ordersText = (n: number) => plural(n, 'zlecenie', 'zlecenia', 'zleceń');

/** Szczegóły poziomu do dymka: mediana, rozrzut, liczba i zakres dat. */
export function levelDetail(l: PriceLevel): string {
  const name =
    l.level === 'relation'
      ? l.label.replace(/^relacji/, 'Relacja')
      : l.level === 'contractor'
        ? 'Klient'
        : l.level === 'all'
          ? 'Cały Marathon'
          : l.label;
  if (l.key === null) return `${name}: brak danych w zleceniu`;
  if (!l.enough || l.medianEurPerKm === null) {
    return `${name}: za mało danych (${ordersText(l.orders)})`;
  }
  const spread =
    l.q1EurPerKm !== null && l.q3EurPerKm !== null
      ? `, połowa zleceń ${eurPerKmNumber(l.q1EurPerKm)}–${eurPerKmText(l.q3EurPerKm)}`
      : '';
  const period = l.periodFrom && l.periodTo ? `, załadunki ${clientDay(l.periodFrom)}–${clientDay(l.periodTo)}` : '';
  return `${name}: mediana ${eurPerKmText(l.medianEurPerKm)}${spread}, ${ordersText(l.orders)}${period}`;
}
